import { useCallback, useState } from "react";
import {
  type AgentSubscriptionPriceMappingPayload,
  type AgentSubscriptionPriceMappingResult,
  upsertAgentSubscriptionPriceMapping,
} from "../../api/admin-revenue";
import { t } from "../../i18n";
import { agentCommercePricingStyles as s } from "./agent-commerce-pricing-styles";

type PricingForm = {
  readonly hashee_sku_id: string;
  readonly stripe_price_id: string;
  readonly stripe_product_id: string;
  readonly reason: string;
};

type PricingField = keyof PricingForm;

const EMPTY_FORM: PricingForm = {
  hashee_sku_id: "",
  stripe_price_id: "",
  stripe_product_id: "",
  reason: "",
};

const AGENT_SKU_PATTERN = /^agent_[a-z0-9_]+$/u;
const STRIPE_PRICE_PATTERN = /^price_[A-Za-z0-9_]+$/u;
const STRIPE_PRODUCT_PATTERN = /^prod_[A-Za-z0-9_]+$/u;

export function AgentCommercePricingPanel(): React.JSX.Element {
  const [form, setForm] = useState<PricingForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [result, setResult] =
    useState<AgentSubscriptionPriceMappingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = isValidPricingForm(form) && !saving;

  const updateField = useCallback(
    (field: PricingField, value: string): void => {
      setForm((current) => ({ ...current, [field]: value }));
    },
    [],
  );

  const submit = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      setResult(await upsertAgentSubscriptionPriceMapping(toPayload(form)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.action_failed"));
    } finally {
      setSaving(false);
    }
  }, [canSubmit, form]);

  return (
    <section style={s.panel}>
      <div style={s.header}>
        <h2 style={s.title}>{t("billing.agent_pricing_title")}</h2>
        <p style={s.description}>{t("billing.agent_pricing_desc")}</p>
      </div>
      {error && <p style={s.error}>{error}</p>}
      {result && (
        <p style={s.success}>
          {t("billing.agent_pricing_success", {
            sku: result.hashee_sku_id,
          })}
        </p>
      )}
      <PricingFields form={form} onFieldChange={updateField} />
      <div style={s.actions}>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => void submit()}
          style={{
            ...s.primaryBtn,
            ...(canSubmit ? undefined : s.primaryBtnDisabled),
          }}
        >
          {saving ? t("common.loading") : t("billing.agent_pricing_save")}
        </button>
      </div>
    </section>
  );
}

function PricingFields({
  form,
  onFieldChange,
}: {
  readonly form: PricingForm;
  readonly onFieldChange: (field: PricingField, value: string) => void;
}): React.JSX.Element {
  return (
    <div style={s.grid}>
      <PricingInput
        field="hashee_sku_id"
        labelKey="billing.agent_pricing_sku"
        value={form.hashee_sku_id}
        onFieldChange={onFieldChange}
      />
      <PricingInput
        field="stripe_price_id"
        labelKey="billing.agent_pricing_stripe_price"
        value={form.stripe_price_id}
        onFieldChange={onFieldChange}
      />
      <PricingInput
        field="stripe_product_id"
        labelKey="billing.agent_pricing_stripe_product"
        value={form.stripe_product_id}
        onFieldChange={onFieldChange}
      />
      <PricingReason value={form.reason} onFieldChange={onFieldChange} />
    </div>
  );
}

function PricingInput({
  field,
  labelKey,
  value,
  onFieldChange,
}: {
  readonly field: Exclude<PricingField, "reason">;
  readonly labelKey: string;
  readonly value: string;
  readonly onFieldChange: (field: PricingField, value: string) => void;
}): React.JSX.Element {
  const id = `agent-pricing-${field}`;
  return (
    <label htmlFor={id} style={s.field}>
      <span style={s.label}>{t(labelKey)}</span>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(event) => onFieldChange(field, event.target.value)}
        style={s.input}
      />
    </label>
  );
}

function PricingReason({
  value,
  onFieldChange,
}: {
  readonly value: string;
  readonly onFieldChange: (field: PricingField, value: string) => void;
}): React.JSX.Element {
  return (
    <label htmlFor="agent-pricing-reason" style={s.fieldFull}>
      <span style={s.label}>{t("billing.agent_pricing_reason")}</span>
      <textarea
        id="agent-pricing-reason"
        rows={3}
        value={value}
        onChange={(event) => onFieldChange("reason", event.target.value)}
        style={s.textarea}
      />
    </label>
  );
}

function toPayload(form: PricingForm): AgentSubscriptionPriceMappingPayload {
  return {
    hashee_sku_id: form.hashee_sku_id.trim(),
    stripe_price_id: form.stripe_price_id.trim(),
    stripe_product_id: form.stripe_product_id.trim(),
    reason: form.reason.trim(),
  };
}

function isValidPricingForm(form: PricingForm): boolean {
  return (
    AGENT_SKU_PATTERN.test(form.hashee_sku_id.trim()) &&
    STRIPE_PRICE_PATTERN.test(form.stripe_price_id.trim()) &&
    STRIPE_PRODUCT_PATTERN.test(form.stripe_product_id.trim()) &&
    form.reason.trim().length >= 10 &&
    form.reason.trim().length <= 2000
  );
}
